import React, { useState } from 'react';
import { MemberRegistrationForm } from '../../types';
import { toTitleCase } from '../../utils/textNormalization';

interface GuardianDetailsStepProps {
  formData: MemberRegistrationForm;
  updateFormData: (updates: Partial<MemberRegistrationForm>) => void;
  onNext: () => void;
  onBack: () => void;
}

const GuardianDetailsStep: React.FC<GuardianDetailsStepProps> = ({
  formData,
  updateFormData,
  onNext,
  onBack,
}) => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [guardianWaiverConfirmed, setGuardianWaiverConfirmed] = useState(false);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.guardianFirstName?.trim()) newErrors.guardianFirstName = 'Guardian first name is required';
    if (!formData.guardianLastName?.trim()) newErrors.guardianLastName = 'Guardian last name is required';
    if (!formData.guardianEmail?.trim()) {
      newErrors.guardianEmail = 'Guardian email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.guardianEmail)) {
      newErrors.guardianEmail = 'Invalid email format';
    }
    if (!formData.guardianPhone?.trim()) newErrors.guardianPhone = 'Guardian phone number is required';
    if (!guardianWaiverConfirmed) newErrors.waiver = 'You must confirm the waiver on behalf of the junior member';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateForm()) onNext();
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-800 mb-2">Guardian Details</h2>
      <p className="text-gray-600 mb-6">
        As the junior member is 16 or under on 1 January of the membership year, a parent or guardian must provide their details.
      </p>

      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Guardian First Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.guardianFirstName || ''}
              onChange={(e) => updateFormData({ guardianFirstName: toTitleCase(e.target.value) })}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-fishing-blue focus:border-transparent ${
                errors.guardianFirstName ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="First name"
            />
            {errors.guardianFirstName && <p className="text-red-500 text-sm mt-1">{errors.guardianFirstName}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Guardian Last Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.guardianLastName || ''}
              onChange={(e) => updateFormData({ guardianLastName: toTitleCase(e.target.value) })}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-fishing-blue focus:border-transparent ${
                errors.guardianLastName ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Last name"
            />
            {errors.guardianLastName && <p className="text-red-500 text-sm mt-1">{errors.guardianLastName}</p>}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Guardian Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            value={formData.guardianEmail || ''}
            onChange={(e) => updateFormData({ guardianEmail: e.target.value })}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-fishing-blue focus:border-transparent ${
              errors.guardianEmail ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="guardian@example.com"
          />
          {errors.guardianEmail && <p className="text-red-500 text-sm mt-1">{errors.guardianEmail}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Guardian Phone <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            value={formData.guardianPhone || ''}
            onChange={(e) => updateFormData({ guardianPhone: e.target.value })}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-fishing-blue focus:border-transparent ${
              errors.guardianPhone ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="0412 345 678"
          />
          {errors.guardianPhone && <p className="text-red-500 text-sm mt-1">{errors.guardianPhone}</p>}
        </div>

        <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <label className="flex items-start cursor-pointer">
            <input
              type="checkbox"
              checked={guardianWaiverConfirmed}
              onChange={(e) => {
                setGuardianWaiverConfirmed(e.target.checked);
                if (errors.waiver) {
                  setErrors(prev => { const n = { ...prev }; delete n.waiver; return n; });
                }
              }}
              className="mt-1 mr-3 h-5 w-5 text-fishing-blue focus:ring-fishing-blue border-gray-300 rounded"
            />
            <span className="text-gray-700">
              I, as the guardian, confirm the waiver on behalf of the junior member.
              <span className="text-red-500 ml-1">*</span>
            </span>
          </label>
          {errors.waiver && <p className="text-red-500 text-sm mt-2">{errors.waiver}</p>}
        </div>
      </div>

      <div className="flex justify-between mt-8">
        <button
          onClick={onBack}
          className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Back
        </button>
        <button
          onClick={handleNext}
          className="px-6 py-2 bg-fishing-blue hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default GuardianDetailsStep;
